import { useCallback, useMemo } from 'react';
import { Typography } from 'antd';

import FilterBuilder from '../common/FilterBuilder';
import FilterChips from '../common/FilterChips';
import useCollectionTags from '../../hooks/useCollectionTags';
import { useOptionalTags } from '../../contexts/TagsContext';
import { buildFilterFields, tagsFilterField } from '../../utils/kqlBuilder';
import { lookupSourceCollectionId, lookupTargetCardIds } from '../../utils/lookupFilter';

/**
 * A related list's default filter: the list's own builder and chips over the
 * SOURCE card's items (docs/plans/ list-queries, KPR-720). The same pieces a
 * lookup's picker filter uses (LookupFilterOptions): the filter is about the
 * source card, so its conditions name that card's elements, its child cards'
 * and its ancestors' — never this card's — and a condition is written by the
 * element's id, which is how the API stores it (a rename never breaks it).
 *
 * Tags are the collection's own, the vocabulary the API compiles the stored
 * filter against (`tagMode: 'collection'`): those of the collection that
 * holds the source card, a private tag never offered. `me` is held by the
 * builder: the filter is read for every viewer at once.
 */

const { Text } = Typography;

export const FILTER_ME_NOTE = 'This filter can’t use me: the same filter is read for everyone who opens the item.';
const NO_TAGS = Object.freeze([]);

export default function RelatedListFilter({
	sourceCardId,
	filter = '',
	onChange,
	cardsById = null,
	getElementsForCard = null,
	collectionId = null,
	disabled = false,
}) {
	const sourceDef = sourceCardId ? cardsById?.[sourceCardId] || null : null;
	const homeCollectionId = sourceCardId ? lookupSourceCollectionId(sourceDef, collectionId) || null : null;
	const tagsOn = Boolean(sourceCardId && homeCollectionId);
	const { tags: collectionTags, loaded: tagsLoaded } = useCollectionTags(homeCollectionId, { enabled: tagsOn });
	const myTags = useOptionalTags()?.tags || NO_TAGS;
	const tagsField = useMemo(() => (tagsOn ? tagsFilterField({
		tags: collectionTags,
		privateTags: myTags,
		mode: 'collection',
		loaded: tagsLoaded,
	}) : null), [tagsOn, collectionTags, myTags, tagsLoaded]);
	const fields = useMemo(() => {
		if (!sourceCardId) return [];
		const cardIds = lookupTargetCardIds(cardsById, sourceCardId);
		const cards = [...cardIds].map(id => cardsById?.[id] || { _id: id });
		return [...buildFilterFields({ cards, cardIds, getElementsForCard, keepKeys: true }), ...(tagsField ? [tagsField] : [])];
	}, [sourceCardId, cardsById, getElementsForCard, tagsField]);

	const apply = useCallback((text) => onChange(`${text || ''}`.trim() || null), [onChange]);

	if (!sourceCardId) {
		return <Text type='secondary' className='related-list__hint'>Choose a source card to filter its items.</Text>;
	}
	return (
		<div className='related-list__filter' data-testid='related-list-filter'>
			{filter ? (
				<FilterChips
					query={filter}
					fields={fields}
					onChange={disabled ? undefined : apply}
					readOnly={disabled}
					className='related-list__chips'
				/>
			) : null}
			{disabled ? null : (
				<FilterBuilder
					fields={fields}
					query={filter || ''}
					onApply={apply}
					collectionId={homeCollectionId}
					emptyText='No conditions yet — every item that links here is listed.'
					disallowMe
					meDisallowedNote={FILTER_ME_NOTE}
				/>
			)}
			{filter ? null : (
				<Text type='secondary' className='related-list__hint'>
					Every item that links here is listed. Add a condition to list only some.
				</Text>
			)}
		</div>
	);
}
